import type { ReactNode } from "react";
import clsx from "clsx";
import { Loader2 } from "lucide-react";
import { useT } from "../i18n";

export function Spinner({ className = "h-5 w-5" }: { className?: string }) {
  return <Loader2 className={`inline-block animate-spin text-fg-3 ${className}`} aria-hidden="true" />;
}

export type EmptyStateKind = "first-use" | "no-results" | "all-done";

/**
 * Empty states that teach (§3). `first-use` lays out a numbered path (steps) and a primary action;
 * `no-results` suggests what to change; `all-done` says when to come back.
 * Legacy props `description`, `action` and `icon` keep working (`body`, `primary`).
 */
export function EmptyState({ kind = "no-results", title, body, description, steps, primary, secondary, action, icon, className }: {
  kind?: EmptyStateKind; title: ReactNode; body?: ReactNode; description?: ReactNode; steps?: ReactNode[]; primary?: ReactNode; secondary?: ReactNode; action?: ReactNode; icon?: ReactNode; className?: string;
}) {
  const text = body ?? description;
  const first = kind === "first-use";
  return (
    <div className={clsx("flex flex-col gap-3", first ? "items-start p-7" : "items-start px-6 py-8", className)}>
      {icon && <div className="text-fg-3">{icon}</div>}
      <h2 className={first ? "text-[20px] font-bold leading-7" : "text-h2"}>{title}</h2>
      {text && <p className="max-w-prose text-fg-2">{text}</p>}
      {steps && steps.length > 0 && (
        <ol className="mt-1 flex flex-col gap-2.5">
          {steps.map((s, i) => (
            <li key={i} className="flex items-center gap-3">
              <span aria-hidden="true" className={clsx("inline-flex h-6 w-6 flex-none items-center justify-center rounded-full text-xs font-bold", i === 0 ? "iga-tone-ok" : "iga-tone-neutral")}>{i + 1}</span>
              <span>{s}</span>
            </li>
          ))}
        </ol>
      )}
      {(primary || secondary || action) && <div className="mt-1 flex flex-wrap items-center gap-2.5">{primary ?? action}{secondary}</div>}
    </div>
  );
}

export function ErrorBox({ error }: { error: unknown }) {
  if (!error) return null;
  const msg = error instanceof Error ? error.message : String(error);
  return <div className="rounded-control bg-bad-soft px-3 py-2 text-fg" role="alert"><span className="font-semibold text-bad">{msg}</span></div>;
}

export function Loading({ label }: { label?: string }) {
  const t = useT();
  return <div className="flex items-center gap-2 py-10 text-fg-3" role="status"><Spinner /> {label ?? t("ui.loading")}</div>;
}

export function Code({ children, className }: { children: ReactNode; className?: string }) {
  return <code className={clsx("rounded-tag bg-surface-2 px-1.5 py-0.5 font-mono text-[12.5px] text-fg-2", className)}>{children}</code>;
}

/** Column header / group label style (12/16/600 uppercase). */
export function Label({ children, className, as: Tag = "div" }: { children: ReactNode; className?: string; as?: "div" | "span" | "h2" | "h3" }) {
  return <Tag className={clsx("text-label text-fg-3", className)}>{children}</Tag>;
}

/** "View only" label for read-only pages (§3 NotAvailable). */
export function ViewOnly({ className }: { className?: string }) {
  const t = useT();
  return <span className={clsx("inline-flex h-6 items-center rounded-tag border border-line bg-surface-2 px-2 text-xs font-medium text-fg-2", className)}>{t("ui.viewOnly")}</span>;
}
